import {useCallback, useEffect, useRef} from 'react'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'

// Reasoning efforts the model advertises: undefined while models load/unknown, [] when the model has no reasoning support.
export function useModelSupportedReasoningEfforts(selectedModelId: string | undefined): string[] | undefined {
  const {models} = useAgentModels()
  if (!selectedModelId || !models || models.length === 0) return undefined
  const model = models.find(candidate => candidate.id === selectedModelId)
  if (!model) return undefined
  return model.capabilities?.supports?.reasoning_effort ?? []
}

// Returns the reasoning effort to keep for a model: preserved when support is unknown
// (models still loading), cleared when the model advertises a list that excludes it.
export function reasoningEffortForModel(
  effort: ReasoningEffort | undefined,
  supportedEfforts: string[] | undefined,
): ReasoningEffort | undefined {
  if (!effort || !supportedEfforts) return effort
  return supportedEfforts.includes(effort) ? effort : undefined
}

// Wraps a model setter so switching models also clears a reasoning effort the new model can't honor.
export function useSelectModelClearingReasoningEffort(
  setSelectedModelId: (modelId: string) => void,
  selectedReasoningEffort: ReasoningEffort | undefined,
  setSelectedReasoningEffort: (effort: ReasoningEffort | undefined) => void,
): (modelId: string) => void {
  const {models} = useAgentModels()
  // Read the latest effort through a ref so the returned callback identity stays
  // stable across reasoning-effort changes. Otherwise ModelPicker re-renders on
  // every effort pick, since this callback is its onUpdateModel.
  const selectedReasoningEffortRef = useRef(selectedReasoningEffort)
  useEffect(() => {
    selectedReasoningEffortRef.current = selectedReasoningEffort
  }, [selectedReasoningEffort])
  return useCallback(
    (modelId: string) => {
      setSelectedModelId(modelId)
      const model = models.find(candidate => candidate.id === modelId)
      const supported = model ? (model.capabilities?.supports?.reasoning_effort ?? []) : undefined
      const current = selectedReasoningEffortRef.current
      const next = reasoningEffortForModel(current, supported)
      if (next !== current) {
        setSelectedReasoningEffort(next)
      }
    },
    [models, setSelectedModelId, setSelectedReasoningEffort],
  )
}

// Reactively clears a selected reasoning effort once we learn the current model
// can't honor it — e.g. a value restored from localStorage before models loaded,
// or a model that advertises no reasoning support. Mirrors ModelPicker's
// auto-fallback so the picker button never shows an effort missing from the
// dropdown, and we never submit an unsupported value to the backend.
export function useReasoningEffortAutoFallback(
  selectedReasoningEffort: ReasoningEffort | undefined,
  supportedReasoningEfforts: string[] | undefined,
  setSelectedReasoningEffort: (effort: ReasoningEffort | undefined) => void,
): void {
  useEffect(() => {
    const next = reasoningEffortForModel(selectedReasoningEffort, supportedReasoningEfforts)
    if (next !== selectedReasoningEffort) {
      setSelectedReasoningEffort(next)
    }
  }, [selectedReasoningEffort, supportedReasoningEfforts, setSelectedReasoningEffort])
}
